<script lang="ts">
	import type { HTMLInputAttributes, HTMLInputTypeAttribute } from 'svelte/elements';
	import { cn, type WithElementRef } from '$lib/shadcn/utils.js';
	import { tv } from 'tailwind-variants';

	type InputType = Exclude<HTMLInputTypeAttribute, 'file'>;

	type Props = WithElementRef<
		Omit<HTMLInputAttributes, 'type'> &
			(
				| { type: 'file'; files?: FileList; variant?: undefined }
				| { type?: InputType; files?: undefined; variant?: 'default' | 'tonal' }
			)
	>;

	let {
		ref = $bindable(null),
		value = $bindable(),
		type,
		variant,
		files = $bindable(),
		class: className,
		'data-slot': dataSlot = 'input',
		...restProps
	}: Props = $props();

	export const inputVariant = tv({
		base: 'flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
		variants: {
			variant: {
				default:
					'border-input bg-background shadow-xs selection:bg-primary dark:bg-input/30 selection:text-primary-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] ',
				tonal:
					'border-none preset-tonal selection:bg-primary/80 selection:text-primary-foreground ring-offset-background placeholder:text-muted-foreground-3 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px] '
			},
			size: {
				default: ''
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	});
</script>

{#if type === 'file'}
	<input
		bind:this={ref}
		data-slot={dataSlot}
		class={cn(
			'selection:bg-primary dark:bg-input/30 selection:text-primary-foreground border-input ring-offset-background placeholder:text-muted-foreground flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 pt-1.5 text-sm font-medium shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
			'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
			'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
			className
		)}
		type="file"
		bind:files
		bind:value
		{...restProps}
	/>
{:else}
	<input
		bind:this={ref}
		data-slot={dataSlot}
		class={cn(inputVariant({ variant, size: 'default' }), className)}
		{type}
		bind:value
		{...restProps}
	/>
{/if}
